/**
 * Correction Queue Page
 * Route: /corrections
 * Age-ordered cards with promote/reject/defer actions.
 * Same resolve choke point as MCP tools.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { Card, Badge, Button, Spinner, Select, Textarea, Modal } from 'flowbite-react';
import {
  HiCheck,
  HiX,
  HiClock,
  HiRefresh,
  HiExclamation,
  HiChevronUp,
  HiChevronDown,
  HiDocumentText,
  HiUser,
  HiCode,
} from 'react-icons/hi';
import { correctionsApi } from '../api/corrections';
import type { CorrectionItem } from '../api/corrections';

const REJECTION_REASONS = [
  { value: 'mistaken', label: 'Mistaken', description: 'The correction was based on a misunderstanding.' },
  { value: 'duplicate', label: 'Duplicate', description: 'Already captured by an existing standard or correction.' },
  { value: 'out-of-scope', label: 'Out of Scope', description: 'Not relevant to this company\'s standards corpus.' },
  { value: 'withdrawn', label: 'Withdrawn', description: 'The contributor retracted the correction.' },
] as const;

function formatAge(hours: number): string {
  if (hours < 1) return '<1h';
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days === 1) return '1 day';
  return `${days} days`;
}

function getAgeBadgeColor(hours: number): string {
  if (hours > 168) return 'failure';
  if (hours > 48) return 'warning';
  return 'info';
}

function getMaturityBadgeColor(maturity: string): string {
  switch (maturity) {
    case 'enforced': return 'success';
    case 'validated': return 'info';
    case 'solidified': return 'warning';
    case 'provisional': return 'gray';
    default: return 'gray';
  }
}

interface CorrectionGroup {
  key: string;
  label: string;
  items: CorrectionItem[];
}

const CorrectionQueue: React.FC = () => {
  const [corrections, setCorrections] = useState<CorrectionItem[]>([]);
  const [statusFilter, setStatusFilter] = useState<string>('recorded');
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isActioning, setIsActioning] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [isBatchProcessing, setIsBatchProcessing] = useState(false);
  const [viewMode, setViewMode] = useState<'flat' | 'session' | 'standard'>('flat');
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  // Rejection modal
  const [rejectTarget, setRejectTarget] = useState<CorrectionItem | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectFeedback, setRejectFeedback] = useState('');

  // Defer modal
  const [deferTarget, setDeferTarget] = useState<CorrectionItem | null>(null);
  const [deferDate, setDeferDate] = useState('');
  const [deferReason, setDeferReason] = useState('');

  const loadCorrections = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await correctionsApi.list(statusFilter);
      if (response.success && response.data) {
        const data = (response.data as any).data || response.data;
        setCorrections(data.items || []);
        setCounts(data.counts || {});
      } else {
        setError(response.error || 'Failed to load corrections');
      }
    } catch (err) {
      setError('Failed to load correction queue');
    } finally {
      setIsLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    loadCorrections();
  }, [loadCorrections]);

  const showSuccess = (msg: string) => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  const handlePromote = async (item: CorrectionItem) => {
    setIsActioning(item.correction_id);
    setError(null);
    try {
      const response = await correctionsApi.resolve({
        correction_id: item.correction_id,
        action: 'promote',
        reason: item.note || 'Promoted via console',
      });
      if (response.success) {
        setCorrections(prev => prev.filter(c => c.correction_id !== item.correction_id));
        setCounts(prev => ({
          ...prev,
          recorded: (prev.recorded || 0) - 1,
          promoted: (prev.promoted || 0) + 1,
        }));
        showSuccess(`Correction ${item.correction_id.slice(0, 8)} promoted`);
      } else {
        setError(response.error || 'Failed to promote');
      }
    } catch (err) {
      setError('Failed to promote correction');
    } finally {
      setIsActioning(null);
    }
  };

  const handleRejectSubmit = async () => {
    if (!rejectTarget || !rejectReason) return;
    const item = rejectTarget;
    setRejectTarget(null);
    setIsActioning(item.correction_id);
    setError(null);
    try {
      const response = await correctionsApi.resolve({
        correction_id: item.correction_id,
        action: 'reject',
        reason: rejectFeedback ? `${rejectReason}: ${rejectFeedback}` : rejectReason,
      });
      if (response.success) {
        setCorrections(prev => prev.filter(c => c.correction_id !== item.correction_id));
        setCounts(prev => ({
          ...prev,
          recorded: (prev.recorded || 0) - 1,
          rejected: (prev.rejected || 0) + 1,
        }));
        showSuccess(`Correction ${item.correction_id.slice(0, 8)} rejected`);
      } else {
        setError(response.error || 'Failed to reject');
      }
    } catch (err) {
      setError('Failed to reject correction');
    } finally {
      setIsActioning(null);
      setRejectReason('');
      setRejectFeedback('');
    }
  };

  const handleDeferSubmit = async () => {
    if (!deferTarget || !deferDate || !deferReason) return;
    const item = deferTarget;
    setDeferTarget(null);
    setIsActioning(item.correction_id);
    setError(null);
    try {
      const response = await correctionsApi.resolve({
        correction_id: item.correction_id,
        action: 'defer',
        reason: deferReason,
        defer_until: new Date(deferDate).toISOString(),
      });
      if (response.success) {
        setCorrections(prev => prev.filter(c => c.correction_id !== item.correction_id));
        setCounts(prev => ({
          ...prev,
          recorded: (prev.recorded || 0) - 1,
          deferred: (prev.deferred || 0) + 1,
        }));
        showSuccess(`Correction ${item.correction_id.slice(0, 8)} deferred until ${deferDate}`);
      } else {
        setError(response.error || 'Failed to defer');
      }
    } catch (err) {
      setError('Failed to defer correction');
    } finally {
      setIsActioning(null);
      setDeferDate('');
      setDeferReason('');
    }
  };

  const toggleSelect = (id: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === corrections.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(corrections.map(c => c.correction_id)));
    }
  };

  const handleBatchPromote = async () => {
    if (selected.size === 0) return;
    setIsBatchProcessing(true);
    setError(null);
    let promoted = 0;
    let failed = 0;
    for (const id of selected) {
      const item = corrections.find(c => c.correction_id === id);
      if (!item) continue;
      try {
        const response = await correctionsApi.resolve({
          correction_id: id,
          action: 'promote',
          reason: item.note || 'Batch promoted via console',
        });
        if (response.success) promoted++;
        else failed++;
      } catch {
        failed++;
      }
    }
    setSelected(new Set());
    setIsBatchProcessing(false);
    if (failed > 0) {
      setError(`Batch promote: ${promoted} succeeded, ${failed} failed`);
    } else {
      showSuccess(`Batch promoted ${promoted} corrections`);
    }
    await loadCorrections();
  };

  const sessionStats = corrections.reduce<Record<string, number>>((acc, c) => {
    const key = c.session_id || 'unknown';
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});

  const uniqueSessions = Object.keys(sessionStats).length;
  const topSession = Object.entries(sessionStats).sort((a, b) => b[1] - a[1])[0];

  const groups = React.useMemo((): CorrectionGroup[] => {
    if (viewMode === 'flat') return [];
    const groupMap = new Map<string, CorrectionItem[]>();
    for (const c of corrections) {
      const key = viewMode === 'session'
        ? (c.session_id || 'unknown')
        : (c.matched_standard_id || 'unmatched');
      if (!groupMap.has(key)) groupMap.set(key, []);
      groupMap.get(key)!.push(c);
    }
    return Array.from(groupMap.entries())
      .map(([key, items]) => ({
        key,
        label: viewMode === 'session'
          ? `Session ${key.slice(0, 8)}`
          : items[0]?.matched_standard?.element || 'No matched standard',
        items,
      }))
      .sort((a, b) => b.items.length - a.items.length);
  }, [corrections, viewMode]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Correction Queue</h1>
          <p className="mt-1 text-sm text-gray-500">
            Review corrections recorded during AI coding sessions
          </p>
        </div>
        <Button color="light" onClick={loadCorrections} disabled={isLoading}>
          <HiRefresh className={`mr-2 h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Messages */}
      {error && (
        <div className="p-4 text-red-700 bg-red-100 rounded-md flex items-center gap-2">
          <HiExclamation className="h-5 w-5 flex-shrink-0" />
          {error}
        </div>
      )}
      {successMessage && (
        <div className="p-4 text-green-700 bg-green-100 rounded-md flex items-center gap-2">
          <HiCheck className="h-5 w-5 flex-shrink-0" />
          {successMessage}
        </div>
      )}

      {/* Status Tabs + Stats */}
      <Card>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-3">
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="recorded">Pending ({counts.recorded || 0})</option>
              <option value="promoted">Promoted ({counts.promoted || 0})</option>
              <option value="rejected">Rejected ({counts.rejected || 0})</option>
              <option value="deferred">Deferred ({counts.deferred || 0})</option>
            </Select>
            <div className="flex">
              {(['flat', 'session', 'standard'] as const).map((mode, i) => (
                <button
                  key={mode}
                  onClick={() => { setViewMode(mode); if (mode !== 'flat') setExpandedGroups(new Set()); }}
                  className={`px-2 py-1.5 text-xs border ${
                    i === 0 ? 'rounded-l' : i === 2 ? 'rounded-r border-l-0' : 'border-l-0'
                  } ${viewMode === mode
                    ? 'bg-gray-100 text-gray-800 border-gray-300 font-medium'
                    : 'bg-white text-gray-500 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {mode === 'flat' ? 'Flat' : mode === 'session' ? 'By Session' : 'By Standard'}
                </button>
              ))}
            </div>
          </div>
          {statusFilter === 'recorded' && corrections.length > 0 && (
            <div className="flex items-center gap-4 text-sm text-gray-500">
              <span>{corrections.length} corrections from {uniqueSessions} sessions</span>
              {topSession && topSession[1] > 1 && (
                <span className="text-xs text-gray-400">
                  Top session: {topSession[0].slice(0, 8)}... ({topSession[1]} corrections)
                </span>
              )}
            </div>
          )}
        </div>
      </Card>

      {/* Batch Action Bar */}
      {selected.size > 0 && (statusFilter === 'recorded' || statusFilter === 'deferred') && (
        <div className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <span className="text-sm font-medium text-blue-700">
            {selected.size} selected
          </span>
          <Button
            color="success"
            size="xs"
            onClick={handleBatchPromote}
            disabled={isBatchProcessing}
          >
            {isBatchProcessing ? <Spinner size="xs" className="mr-1" /> : <HiCheck className="mr-1 h-3 w-3" />}
            Batch Promote
          </Button>
          <Button
            color="gray"
            size="xs"
            onClick={() => setSelected(new Set())}
            disabled={isBatchProcessing}
          >
            Clear Selection
          </Button>
          <span className="ml-auto text-xs text-blue-500">
            {selected.size} of {corrections.length} corrections
          </span>
        </div>
      )}

      {/* Correction Cards */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <Spinner size="xl" />
        </div>
      ) : corrections.length === 0 ? (
        <Card>
          <div className="text-center py-12">
            <HiDocumentText className="mx-auto h-12 w-12 text-gray-300" />
            <h3 className="mt-4 text-sm font-medium text-gray-900">No corrections</h3>
            <p className="mt-1 text-sm text-gray-500">
              {statusFilter === 'recorded'
                ? 'No pending corrections to review.'
                : `No ${statusFilter} corrections found.`}
            </p>
          </div>
        </Card>
      ) : viewMode !== 'flat' ? (
        <div className="space-y-3">
          {groups.map(group => {
            const isGroupExpanded = expandedGroups.has(group.key);
            const groupSelected = group.items.filter(i => selected.has(i.correction_id));
            const allGroupSelected = group.items.length > 0 && groupSelected.length === group.items.length;
            const someGroupSelected = groupSelected.length > 0 && !allGroupSelected;
            const isActionable = statusFilter === 'recorded' || statusFilter === 'deferred';
            return (
              <div key={group.key} className="border border-gray-200 rounded-lg bg-white">
                <div
                  className="flex items-center gap-3 p-3 cursor-pointer hover:bg-gray-50"
                  onClick={() => setExpandedGroups(prev => {
                    const next = new Set(prev);
                    if (next.has(group.key)) next.delete(group.key);
                    else next.add(group.key);
                    return next;
                  })}
                >
                  {isActionable && (
                    <input
                      type="checkbox"
                      checked={allGroupSelected}
                      ref={el => { if (el) el.indeterminate = someGroupSelected; }}
                      onChange={e => {
                        e.stopPropagation();
                        setSelected(prev => {
                          const next = new Set(prev);
                          if (allGroupSelected) {
                            group.items.forEach(i => next.delete(i.correction_id));
                          } else {
                            group.items.forEach(i => next.add(i.correction_id));
                          }
                          return next;
                        });
                      }}
                      onClick={e => e.stopPropagation()}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  )}
                  {isGroupExpanded
                    ? <HiChevronUp className="h-4 w-4 text-gray-400" />
                    : <HiChevronDown className="h-4 w-4 text-gray-400" />
                  }
                  <Badge color="indigo" className="text-xs font-semibold">{group.label}</Badge>
                  <span className="text-sm text-gray-600 font-medium">{group.items.length} correction{group.items.length !== 1 ? 's' : ''}</span>
                </div>
                {isGroupExpanded && (
                  <div className="border-t border-gray-100 px-3 pb-3 pt-1 space-y-2">
                    {group.items.map(item => {
                      const isProcessing = isActioning === item.correction_id;
                      return (
                        <div
                          key={item.correction_id}
                          className={`rounded border p-2 text-sm ${
                            selected.has(item.correction_id) ? 'border-blue-300 bg-blue-50/30' : 'border-gray-100 bg-gray-50/30'
                          }`}
                        >
                          <div className="flex items-start gap-2">
                            {isActionable && (
                              <input
                                type="checkbox"
                                checked={selected.has(item.correction_id)}
                                onChange={() => toggleSelect(item.correction_id)}
                                className="mt-0.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-mono text-xs text-gray-500">{item.correction_id.slice(0, 8)}</span>
                                <Badge color={getAgeBadgeColor(item.age_hours)} size="xs">{formatAge(item.age_hours)}</Badge>
                                {item.matched_standard && (
                                  <Badge color={getMaturityBadgeColor(item.matched_standard.maturity)} size="xs">
                                    {item.matched_standard.category}
                                  </Badge>
                                )}
                              </div>
                              {item.note && <p className="text-gray-700 mt-0.5 line-clamp-2">{item.note}</p>}
                            </div>
                            {isActionable && (
                              <div className="flex gap-1 flex-shrink-0">
                                <button
                                  onClick={() => handlePromote(item)}
                                  disabled={isProcessing}
                                  className="p-1 rounded hover:bg-green-100 text-green-600 disabled:opacity-50"
                                  title="Promote"
                                >
                                  <HiCheck className="h-3.5 w-3.5" />
                                </button>
                                <button
                                  onClick={() => setRejectTarget(item)}
                                  disabled={isProcessing}
                                  className="p-1 rounded hover:bg-red-100 text-red-600 disabled:opacity-50"
                                  title="Reject"
                                >
                                  <HiX className="h-3.5 w-3.5" />
                                </button>
                                <button
                                  onClick={() => setDeferTarget(item)}
                                  disabled={isProcessing}
                                  className="p-1 rounded hover:bg-amber-100 text-amber-600 disabled:opacity-50"
                                  title="Defer"
                                >
                                  <HiClock className="h-3.5 w-3.5" />
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="space-y-4">
          {(statusFilter === 'recorded' || statusFilter === 'deferred') && corrections.length > 1 && (
            <div className="flex items-center gap-2 px-1">
              <input
                type="checkbox"
                checked={selected.size === corrections.length && corrections.length > 0}
                onChange={toggleSelectAll}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-xs text-gray-500">Select all</span>
            </div>
          )}
          {corrections.map((item) => {
            const isExpanded = expandedId === item.correction_id;
            const isProcessing = isActioning === item.correction_id;
            const isActionable = statusFilter === 'recorded' || statusFilter === 'deferred';

            return (
              <Card key={item.correction_id}>
                <div className="space-y-3">
                  {/* Card Header */}
                  <div className="flex items-start justify-between gap-4">
                    {isActionable && (
                      <input
                        type="checkbox"
                        checked={selected.has(item.correction_id)}
                        onChange={() => toggleSelect(item.correction_id)}
                        className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                    )}
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-sm text-gray-700">
                          {item.correction_id.slice(0, 8)}
                        </span>
                        <Badge color={getAgeBadgeColor(item.age_hours)}>
                          <HiClock className="mr-1 h-3 w-3 inline" />
                          {formatAge(item.age_hours)}
                        </Badge>
                        {item.matched_standard && (
                          <Badge color={getMaturityBadgeColor(item.matched_standard.maturity)}>
                            {item.matched_standard.category}
                          </Badge>
                        )}
                        {item.file_context && (
                          <Badge color="gray">
                            <HiCode className="mr-1 h-3 w-3 inline" />
                            {item.file_context.split('/').pop()}
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-1 text-xs text-gray-400">
                        <HiUser className="h-3 w-3" />
                        <span>{item.email}</span>
                        {item.session_id && (
                          <span className="font-mono">session:{item.session_id.slice(0, 8)}</span>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    {isActionable && (
                      <div className="flex gap-2 flex-shrink-0">
                        <Button
                          color="success"
                          size="xs"
                          onClick={() => handlePromote(item)}
                          disabled={isProcessing}
                        >
                          {isProcessing ? <Spinner size="xs" className="mr-1" /> : <HiCheck className="mr-1 h-3 w-3" />}
                          Promote
                        </Button>
                        <Button
                          color="failure"
                          size="xs"
                          onClick={() => setRejectTarget(item)}
                          disabled={isProcessing}
                        >
                          <HiX className="mr-1 h-3 w-3" />
                          Reject
                        </Button>
                        <Button
                          color="warning"
                          size="xs"
                          onClick={() => setDeferTarget(item)}
                          disabled={isProcessing}
                        >
                          <HiClock className="mr-1 h-3 w-3" />
                          Defer
                        </Button>
                      </div>
                    )}
                    {!isActionable && item.resolved_by && (
                      <span className="text-xs text-gray-400">
                        by {item.resolved_by}
                      </span>
                    )}
                  </div>

                  {/* Correction Note (always visible) */}
                  {item.note && (
                    <p className="text-sm text-gray-700">{item.note}</p>
                  )}

                  {/* Expand/Collapse for evidence */}
                  <button
                    onClick={() => setExpandedId(isExpanded ? null : item.correction_id)}
                    className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
                  >
                    {isExpanded ? <HiChevronUp className="h-3 w-3" /> : <HiChevronDown className="h-3 w-3" />}
                    {isExpanded ? 'Hide evidence' : 'Show evidence'}
                  </button>

                  {/* Evidence Detail (MM-4) */}
                  {isExpanded && (
                    <div className="space-y-3 border-t pt-3">
                      {/* Original vs Corrected */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div className="bg-red-50 rounded p-3">
                          <span className="text-xs font-medium text-red-600 uppercase tracking-wide">Original Output</span>
                          <p className="text-sm text-gray-700 mt-1 whitespace-pre-wrap">{item.original || '(not recorded)'}</p>
                        </div>
                        <div className="bg-green-50 rounded p-3">
                          <span className="text-xs font-medium text-green-600 uppercase tracking-wide">Corrected Output</span>
                          <p className="text-sm text-gray-700 mt-1 whitespace-pre-wrap">{item.corrected || '(not recorded)'}</p>
                        </div>
                      </div>

                      {/* Matched Standard */}
                      {item.matched_standard && (
                        <div className="bg-blue-50 rounded p-3">
                          <span className="text-xs font-medium text-blue-600 uppercase tracking-wide">Matched Standard</span>
                          <p className="text-sm font-medium text-gray-900 mt-1">{item.matched_standard.element}</p>
                          <p className="text-sm text-gray-600 mt-0.5">{item.matched_standard.rule}</p>
                          <div className="flex gap-2 mt-2">
                            <Badge color={getMaturityBadgeColor(item.matched_standard.maturity)} size="sm">
                              {item.matched_standard.maturity}
                            </Badge>
                            <Badge color="gray" size="sm">{item.matched_standard.category}</Badge>
                          </div>
                        </div>
                      )}

                      {/* File Context */}
                      {item.file_context && (
                        <div className="text-xs text-gray-500">
                          <span className="font-medium">File:</span>{' '}
                          <span className="font-mono">{item.file_context}</span>
                        </div>
                      )}

                      {/* Resolution info (for non-pending views) */}
                      {item.resolution_reason && (
                        <div className="bg-gray-50 rounded p-3">
                          <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Resolution</span>
                          <p className="text-sm text-gray-700 mt-1">{item.resolution_reason}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Rejection Modal */}
      <Modal show={rejectTarget !== null} onClose={() => { setRejectTarget(null); setRejectReason(''); setRejectFeedback(''); }} size="lg">
        <Modal.Header>
          <div className="flex items-center gap-2">
            <HiExclamation className="h-5 w-5 text-red-500" />
            Reject Correction
          </div>
        </Modal.Header>
        <Modal.Body>
          <div className="space-y-5">
            <p className="text-sm text-gray-600">
              Rejecting correction{' '}
              <span className="font-mono font-semibold">{rejectTarget?.correction_id.slice(0, 8)}</span>.
              This writes a governance event but no violation.
            </p>
            <fieldset>
              <legend className="text-sm font-medium text-gray-700 mb-3">Reason for rejection</legend>
              <div className="space-y-3">
                {REJECTION_REASONS.map((r) => (
                  <label
                    key={r.value}
                    className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                      rejectReason === r.value
                        ? 'border-red-300 bg-red-50'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="radio"
                      name="rejectReason"
                      value={r.value}
                      checked={rejectReason === r.value}
                      onChange={(e) => setRejectReason(e.target.value)}
                      className="mt-0.5 text-red-600 focus:ring-red-500"
                    />
                    <div>
                      <span className="text-sm font-medium text-gray-900">{r.label}</span>
                      <p className="text-xs text-gray-500 mt-0.5">{r.description}</p>
                    </div>
                  </label>
                ))}
              </div>
            </fieldset>
            <div>
              <label htmlFor="reject-feedback" className="block text-sm font-medium text-gray-700 mb-1">
                Additional context (optional)
              </label>
              <Textarea
                id="reject-feedback"
                value={rejectFeedback}
                onChange={(e) => setRejectFeedback(e.target.value)}
                placeholder="Any additional context..."
                rows={2}
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div className="flex justify-end gap-3 w-full">
            <Button color="gray" onClick={() => { setRejectTarget(null); setRejectReason(''); setRejectFeedback(''); }}>Cancel</Button>
            <Button color="failure" onClick={handleRejectSubmit} disabled={!rejectReason}>
              Reject Correction
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      {/* Defer Modal */}
      <Modal show={deferTarget !== null} onClose={() => { setDeferTarget(null); setDeferDate(''); setDeferReason(''); }} size="lg">
        <Modal.Header>
          <div className="flex items-center gap-2">
            <HiClock className="h-5 w-5 text-yellow-500" />
            Defer Correction
          </div>
        </Modal.Header>
        <Modal.Body>
          <div className="space-y-5">
            <p className="text-sm text-gray-600">
              Deferring correction{' '}
              <span className="font-mono font-semibold">{deferTarget?.correction_id.slice(0, 8)}</span>.
              It will re-enter the queue on the selected date.
            </p>
            <div>
              <label htmlFor="defer-date" className="block text-sm font-medium text-gray-700 mb-1">
                Re-entry date
              </label>
              <input
                id="defer-date"
                type="date"
                value={deferDate}
                onChange={(e) => setDeferDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                className="block w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
            <div>
              <label htmlFor="defer-reason" className="block text-sm font-medium text-gray-700 mb-1">
                Reason for deferral
              </label>
              <Textarea
                id="defer-reason"
                value={deferReason}
                onChange={(e) => setDeferReason(e.target.value)}
                placeholder="Why defer? e.g., needs more evidence, pending related decision..."
                rows={2}
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div className="flex justify-end gap-3 w-full">
            <Button color="gray" onClick={() => { setDeferTarget(null); setDeferDate(''); setDeferReason(''); }}>Cancel</Button>
            <Button color="warning" onClick={handleDeferSubmit} disabled={!deferDate || !deferReason}>
              Defer Correction
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default CorrectionQueue;
